"use client"

import { useRef, useState, useEffect } from "react"
import Image from "next/image"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { motion, useScroll, useTransform } from "motion/react"
import { ArrowUpRight } from "lucide-react"
import type { PropertyWithCommunity } from "@/types/database"
import { formatPrice, STATUS_LABELS } from "@/types/database"

interface LatestLaunchesSectionProps {
  readonly properties: PropertyWithCommunity[];
  readonly eyebrow?: string | null;
  readonly title?: string | null;
  readonly subtitle?: string | null;
  readonly description?: string | null;
}

export function LatestLaunchesSection({
  properties,
  eyebrow = "Our Properties",
  title = "Latest",
  subtitle = "Launches",
  description = "A curated selection of the most anticipated architectural landmarks reshaping the Dubai skyline.",
}: LatestLaunchesSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)

  // Parallax effect for the section header
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })

  const y = useTransform(scrollYProgress, [0, 1], [100, -100])

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      const maxScroll = scrollWidth - clientWidth
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0
      setScrollProgress(progress)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener("scroll", handleScroll)
      // Trigger once to set initial state
      handleScroll()
      return () => container.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <section
      ref={containerRef}
      className="bg-background text-foreground pt-24 pb-12 relative overflow-hidden"
    >
      {/* Background Texture/Gradient */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent pointer-events-none" />

      <div className="container px-6 md:px-12 mb-12 flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
        <motion.div style={{ y }} className="space-y-4 max-w-xl">
          <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
            {eyebrow}
          </span>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
            {title} <br /> <span className="italic text-primary/50">{subtitle}</span>
          </h2>
        </motion.div>

        <div className="hidden md:block pb-2">
          <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
            {description}
          </p>
        </div>
      </div>

      {/* Native Scroll Container */}
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto pb-12 px-6 md:px-12 gap-4 md:gap-8 no-scrollbar snap-none"
      >
        {properties.map((property) => (
          <div
            key={property.id}
            className="flex-none w-[85vw] md:w-[60vw] lg:w-[45vw] group cursor-pointer"
          >
            <Link href={`/properties/${property.id}`} className="block h-full">
              {/* Image Container */}
              <div className="relative aspect-[16/10] overflow-hidden bg-secondary/10 mb-6 rounded-xl shadow-2xl hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] transition-all duration-300">
                <Image
                  src={property.hero_image_url}
                  alt={property.name}
                  fill
                  sizes="(max-width: 768px) 85vw, (max-width: 1200px) 60vw, 45vw"
                  className="object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105 rounded-xl"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white transform scale-90 group-hover:scale-100 transition-transform duration-500">
                    <ArrowUpRight className="w-8 h-8" />
                  </div>
                </div>

                {/* Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-md text-[10px] uppercase tracking-widest font-bold border border-black/5 text-primary rounded-xl">
                    {STATUS_LABELS[property.status]}
                  </span>
                </div>
              </div>

              {/* Content Below */}
              <div className="flex justify-between items-start border-t border-primary/10 pt-6">
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl mb-1 group-hover:text-secondary transition-colors text-primary">
                    {property.name}
                  </h3>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    {property.location_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium mb-1 text-primary">{formatPrice(property.price_from, property.currency)}</p>
                  {property.estimated_completion && (
                    <p className="text-xs text-muted-foreground">
                      Est. {property.estimated_completion}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          </div>
        ))}

        {/* Right Spacer to ensure last item doesn't hit the edge too hard */}
        <div className="flex-none w-4 md:w-6" />
      </div>

      {/* Bottom Progress/Indicator Line */}
      <div className="container px-6 md:px-12 mt-8">
        <div className="w-full h-px bg-primary/10 relative overflow-hidden">
          {/* Dynamic Scroll Handle - Width fixed at 25%, moves based on scroll progress */}
          <div
            className="absolute top-0 h-full w-1/4 bg-secondary transition-none"
            style={{
              left: `${scrollProgress * 75}%`
            }}
          />
        </div>
        <div className="flex justify-between mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>01</span>
          <span>{properties.length < 10 ? `0${properties.length}` : properties.length}</span>
        </div>
      </div>

    </section>
  )
}
